// Advanced Filter builder — nested AND/OR/NOT tree over the INPACT field
// whitelist. Mirrors the CTS dashboard `FilterBuilder` UX but uses INPACT's
// FilterGroup wire shape (the planner contract) directly instead of CTS's
// flat-rules-with-logic-expression. We don't need CTS's `logic` expression
// because our backend already accepts nested FilterGroups natively.
//
// Depth cap: MAX_FILTER_DEPTH, see lib/filterLogic.ts, mirrored from the
// backend validator. The builder renders NOT wrappers transparently (a
// negated group looks one level deep, not two), so the visual `depth` used
// for indentation is NOT what the backend counts — a separate `nodeDepth` is
// threaded alongside it, counting a NOT wrapper as its own node the same way
// `filter_validation.validate_node` does. The "Add group" button is gated on
// `nodeDepth < MAX_FILTER_DEPTH - 1`, i.e. on whether the group it WOULD
// CREATE could still hold a rule (the new group lands at nodeDepth + 1, a
// rule inside it at nodeDepth + 2) — not merely on whether the new group
// itself fits, which would offer a dead-end group with nowhere to put a
// rule.
// Negation is authored per node via a "Not" toggle that wraps/unwraps the
// node in a single-child NOT group. The Apply button is backstopped against
// `backendRejectionReason` (see lib/filterLogic.ts), which mirrors BOTH
// structural rules `filter_validation.validate_node` enforces — depth and
// NOT arity (a NOT must wrap exactly one child) — for trees that arrive
// already invalid (from the NL planner, a saved view, or a NOT the builder
// keeps read-only because it can't be authored here) without the user
// touching "Add group" at all.

import { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import type {
  FilterGroup,
  FilterLogic,
  FilterOperator,
  FilterRule,
  FilterValue,
} from "../types/inpact";
import { MAX_FILTER_DEPTH, backendRejectionReason, emptyGroup, fieldLabel, isGroup, labelsFromFields, toggleNegation, unwrapNegation } from "../lib/filterLogic";
import { FilterTreePreview } from "./FilterTreePreview";
import { isFilterField, OP_LABELS, opsForKind, type FieldDef, type FieldKind } from "../lib/filterFields";
import { useCountries } from "../hooks/useCountries";
import { useFields } from "../hooks/useFields";
import { flagFor } from "../lib/countryUtils";

interface Props {
  open: boolean;
  initial: FilterGroup;
  onClose: () => void;
  onApply: (next: FilterGroup) => void;
}

// The field catalogue + country list every node editor (ChildNode, GroupNode,
// RuleRow) needs to render field pickers and value editors. Threaded
// identically through every level of the tree rather than as three separate
// props, so it reads as one "options context" object instead of a grab-bag —
// see AdvancedFilterModal's `catalogue` useMemo for how its identity is kept
// stable across keystrokes.
interface FilterCatalogue {
  fields: FieldDef[];
  fieldByKey: Map<string, FieldDef>;
  countries: string[];
}

// ── Default rule when a new row is added ─────────────────────────────
// The catalogue's field order is now server-owned (Task 1's field_catalog.py),
// so the first field's kind is no longer guaranteed to be "picklist" (the old
// hardcoded INPACT_FIELDS[0] always was, which is why op "=" + value "" used
// to be a safe constant). Derive both from the field's own kind so a reorder
// on the backend can't hand RuleRow an op the field doesn't support.
export function defaultRule(fields: FieldDef[]): FilterRule {
  const f = fields.find(isFilterField);
  const kind: FieldKind = f?.kind ?? "picklist";
  const op = opsForKind(kind)[0];
  return { field: f?.key ?? "contact.INPACT_Associate_Status__c", op, value: defaultValueForOp(kind, op) };
}

function defaultValueForOp(kind: FieldKind, op: FilterOperator): FilterValue {
  if (op === "IS NULL" || op === "IS NOT NULL") return null;
  if (op === "BETWEEN") return ["", ""];
  if (op === "IN" || op === "NOT IN" || op === "INCLUDES" || op === "EXCLUDES") return [];
  if (kind === "boolean") return true;
  if (kind === "number") return "";
  return "";
}

// ── Validation: every leaf needs a usable value. ─────────────────────
function isLeafComplete(rule: FilterRule): boolean {
  if (rule.op === "IS NULL" || rule.op === "IS NOT NULL") return true;
  const v = rule.value;
  if (v === null || v === undefined) return false;
  if (Array.isArray(v)) {
    if (rule.op === "BETWEEN") return v.length === 2 && v.every((x) => String(x).length > 0);
    return v.length > 0;
  }
  if (typeof v === "string") return v.trim().length > 0;
  return true;
}

function isGroupComplete(g: FilterGroup): boolean {
  if (g.rules.length === 0) return false;
  for (const r of g.rules) {
    if (isGroup(r)) {
      if (!isGroupComplete(r)) return false;
    } else if (!isLeafComplete(r)) return false;
  }
  return true;
}

function hasOnlyAvailableFilterFields(
  group: FilterGroup,
  fieldByKey: Map<string, FieldDef>,
): boolean {
  return group.rules.every((node) => (
    isGroup(node)
      ? hasOnlyAvailableFilterFields(node, fieldByKey)
      : fieldByKey.has(node.field) && isFilterField(fieldByKey.get(node.field)!)
  ));
}

// ── Renderers ────────────────────────────────────────────────────────

function ValueEditor({
  rule, def, countries, onChange,
}: {
  rule: FilterRule;
  def: FieldDef | null;
  countries: string[];
  onChange: (v: FilterValue) => void;
}) {
  const kind: FieldKind = def?.kind ?? "string";

  if (rule.op === "IS NULL" || rule.op === "IS NOT NULL") return null;

  if (kind === "boolean") {
    return (
      <select
        className="adv-filter__input"
        value={String(rule.value ?? "true")}
        onChange={(e) => onChange(e.target.value === "true")}
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (rule.op === "BETWEEN") {
    const [a, b] = Array.isArray(rule.value) ? rule.value : ["", ""];
    const type = kind === "date" ? "date" : "number";
    return (
      <div className="adv-filter__between">
        <input type={type} className="adv-filter__input"
          value={String(a ?? "")}
          onChange={(e) => onChange([e.target.value, String(b ?? "")])} />
        <span className="adv-filter__sep">–</span>
        <input type={type} className="adv-filter__input"
          value={String(b ?? "")}
          onChange={(e) => onChange([String(a ?? ""), e.target.value])} />
      </div>
    );
  }

  // Set-valued ops
  const isSetOp = rule.op === "IN" || rule.op === "NOT IN"
    || rule.op === "INCLUDES" || rule.op === "EXCLUDES";

  if (isSetOp) {
    const options = kind === "country" ? countries : (def?.options ?? []);
    const current: string[] = Array.isArray(rule.value)
      ? rule.value.map(String)
      : [];
    return (
      <MultiSelect
        options={options}
        selected={current}
        renderLabel={kind === "country" ? (v) => `${flagFor(v)} ${v}` : (v) => v}
        onChange={onChange}
      />
    );
  }

  if (kind === "date") {
    return (
      <input type="date" className="adv-filter__input"
        value={String(rule.value ?? "")}
        onChange={(e) => onChange(e.target.value)} />
    );
  }

  if (kind === "picklist" || kind === "multipicklist") {
    const options = def?.options ?? [];
    const current = String(rule.value ?? "");
    return (
      <select
        className="adv-filter__input"
        value={current}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" disabled>Select…</option>
        {/* A saved rule can hold a value the list no longer contains — an
            admin deactivated or renamed the picklist value, and the
            catalogue only carries the active ones. Without an option for
            it the control falls back to "Select…" while `isLeafComplete`
            still counts the rule as complete and leaves Apply enabled: the
            user would apply a filter whose value the UI never showed them.
            Same fault as the field picker above, one control down. */}
        {current && !options.includes(current) && (
          <option value={current}>{current} (no longer available)</option>
        )}
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  if (kind === "country") {
    const current = String(rule.value ?? "");
    return (
      <select
        className="adv-filter__input"
        value={current}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" disabled>Select…</option>
        {/* Same reason as the picklist above, reached differently: the
            country list is the set of countries the cached slice actually
            contains, so the last contact leaving a country removes it. A
            saved view filtering on it must still show what it filters. */}
        {current && !countries.includes(current) && (
          <option value={current}>{current} (no longer available)</option>
        )}
        {countries.map((o) => (
          <option key={o} value={o}>{flagFor(o)} {o}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      type={kind === "number" ? "number" : "text"}
      className="adv-filter__input"
      value={String(rule.value ?? "")}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function MultiSelect({
  options, selected, renderLabel, onChange,
}: {
  options: readonly string[];
  selected: string[];
  renderLabel: (v: string) => string;
  onChange: (v: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  // A saved rule can hold values the option list no longer contains — an
  // admin deactivated or renamed a picklist value, or the last contact from
  // a country left the slice. Listing only `options` would count such a
  // value in "2 selected" while showing neither it nor a way to remove it:
  // the user cannot see what the filter does, and cannot undo it except by
  // deleting the whole rule. Same fault as the scalar selects above, one
  // control over.
  const listed = useMemo(() => {
    const missing = selected.filter((v) => !options.includes(v));
    return missing.length ? [...missing, ...options] : options;
  }, [options, selected]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return listed;
    return listed.filter((o) => o.toLowerCase().includes(q));
  }, [listed, query]);
  const toggle = (v: string) => {
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };
  return (
    <div className="adv-filter__multi">
      <button type="button" className="adv-filter__input adv-filter__multi-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}>
        {selected.length === 0
          ? <span className="adv-filter__placeholder">Select values…</span>
          : <span>{selected.length} selected</span>}
        <span aria-hidden>▾</span>
      </button>
      {open && (
        <div className="adv-filter__multi-pop">
          <input
            className="adv-filter__input"
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <ul className="adv-filter__multi-list">
            {visible.map((o) => (
              <li key={o}>
                <label>
                  <input type="checkbox"
                    checked={selected.includes(o)}
                    onChange={() => toggle(o)} />
                  <span>
                    {renderLabel(o)}
                    {!options.includes(o) && " (no longer available)"}
                  </span>
                </label>
              </li>
            ))}
            {visible.length === 0 && (
              <li className="adv-filter__multi-empty">No matches</li>
            )}
          </ul>
          <button type="button" className="adv-filter__multi-done"
            onClick={() => setOpen(false)}>Done</button>
        </div>
      )}
    </div>
  );
}

function FieldPicker({
  value, fields, onChange,
}: {
  value: string;
  fields: FieldDef[];
  onChange: (key: string) => void;
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, FieldDef[]>();
    for (const f of fields) {
      const list = map.get(f.group) ?? [];
      list.push(f);
      map.set(f.group, list);
    }
    return [...map.entries()];
  }, [fields]);
  // A rule can name a field the catalogue does not offer — a view saved
  // before the field was retired, an AnaMoni answer from an older turn, a
  // facet the rail still exposes. A <select> whose value matches no option
  // silently displays the FIRST one, so the row would claim to filter
  // something it does not: the user reads "Business Phone" on a rule that is
  // really about Education. Give the unknown key an option of its own.
  const unknown = value !== "" && !fields.some((f) => f.key === value);
  return (
    <select
      className="adv-filter__input adv-filter__field-picker"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Field"
    >
      {unknown && (
        <option value={value}>{fieldLabel(value)} (not available)</option>
      )}
      {grouped.map(([g, fs]) => (
        <optgroup key={g} label={g}>
          {fs.map((f) => {
            const selectable = isFilterField(f);
            const reason = !f.available
              ? f.unavailable_reason ?? "Field is unavailable"
              : "Not approved for filtering";
            return (
              <option key={f.key} value={f.key} disabled={!selectable}>
                {f.label}{selectable ? "" : ` (${reason})`}
              </option>
            );
          })}
        </optgroup>
      ))}
    </select>
  );
}

// The "Not" toggle button: identical markup wherever it appears (a rule row
// or a group header), only the tooltip text differs per context.
function NotToggle({
  negated, onToggle, title,
}: {
  negated: boolean;
  onToggle: () => void;
  title: string;
}) {
  return (
    <button type="button" className="adv-filter__not-toggle"
      aria-pressed={negated} onClick={onToggle} title={title}>Not</button>
  );
}

function RuleRow({
  rule, depth, catalogue, negated, onToggleNegate, onChange, onRemove,
}: {
  rule: FilterRule;
  depth: number;
  catalogue: FilterCatalogue;
  negated: boolean;
  onToggleNegate?: () => void;
  onChange: (next: FilterRule) => void;
  onRemove: () => void;
}) {
  const { fields, fieldByKey, countries } = catalogue;
  const def = fieldByKey.get(rule.field) ?? null;
  const kind: FieldKind = def?.kind ?? "string";
  const ops = opsForKind(kind);
  const complete = isLeafComplete(rule);

  const onFieldChange = (key: string) => {
    const nextDef = fieldByKey.get(key) ?? null;
    const nextKind: FieldKind = nextDef?.kind ?? "string";
    const allowedOps = opsForKind(nextKind);
    const op: FilterOperator = allowedOps.includes(rule.op) ? rule.op : allowedOps[0];
    onChange({ field: key, op, value: defaultValueForOp(nextKind, op) });
  };

  const onOpChange = (op: FilterOperator) => {
    onChange({ ...rule, op, value: defaultValueForOp(kind, op) });
  };

  return (
    <div className="adv-filter__rule" data-depth={depth} data-complete={complete}>
      {onToggleNegate && (
        <NotToggle negated={negated} onToggle={onToggleNegate}
          title="Exclude contacts matching this rule" />
      )}
      <FieldPicker value={rule.field} fields={fields} onChange={onFieldChange} />
      <select
        className="adv-filter__input adv-filter__op"
        value={rule.op}
        onChange={(e) => onOpChange(e.target.value as FilterOperator)}
        aria-label="Operator"
      >
        {/* Same reason as the field picker: an op outside this kind's set
            (a rule built when the field was a different kind, or by a
            surface with its own operator vocabulary) must not be displayed
            as some other operator. */}
        {!ops.includes(rule.op) && (
          <option value={rule.op}>{OP_LABELS[rule.op] ?? rule.op}</option>
        )}
        {ops.map((o) => <option key={o} value={o}>{OP_LABELS[o]}</option>)}
      </select>
      <ValueEditor rule={rule} def={def} countries={countries}
        onChange={(v) => onChange({ ...rule, value: v })} />
      <button type="button" className="adv-filter__remove" onClick={onRemove}
        aria-label="Remove rule">×</button>
    </div>
  );
}

// Renders one child of a group. A NOT wrapper is transparent here: we render
// the negated node itself with the "Not" toggle pressed and re-wrap on change.
// That is what keeps "a NOT wraps exactly one child" true by construction —
// there is no gesture that can add a second child to a NOT.
//
// `depth` is purely visual (drives indentation via `data-depth`). `nodeDepth`
// is the backend depth of the PARENT group this child lives in — used only to
// compute the child's true backend depth for the nested "Add group" gate. A
// negated group child costs two backend nodes (the NOT wrapper, then the
// group itself) even though it renders as one visual level.
//
// `negationLocked` is set by a PARENT GroupNode that is itself frozen (its
// `isNot` read-only branch) to withhold the "Not" toggle from this child
// regardless of whether the child's own shape would otherwise be negatable
// — see GroupNode's `isNot` comment below for why.
function ChildNode({
  node, depth, nodeDepth, catalogue, negationLocked = false, onChange, onRemove,
}: {
  node: FilterRule | FilterGroup;
  depth: number;
  nodeDepth: number;
  catalogue: FilterCatalogue;
  negationLocked?: boolean;
  onChange: (next: FilterRule | FilterGroup) => void;
  onRemove: () => void;
}) {
  const { inner, negated } = unwrapNegation(node);
  // A malformed NOT (multi-child, or NOT{NOT{…}}) reports as un-negated. Offering
  // it a toggle would wrap it in a SECOND NOT, so withhold the toggle entirely.
  // Same when the PARENT group is frozen (`negationLocked`): even a
  // well-formed child would let the user reshape the frozen parent from
  // outside, so withhold it there too.
  const malformedNot = !negated && isGroup(node) && node.logic === "NOT";
  const onToggleNegate = malformedNot || negationLocked
    ? undefined
    : () => onChange(toggleNegation(node));
  const emit = (next: FilterRule | FilterGroup) =>
    onChange(negated ? { logic: "NOT", rules: [next] } : next);

  if (isGroup(inner)) {
    return (
      <GroupNode node={inner} depth={depth + 1} negated={negated}
        nodeDepth={nodeDepth + (negated ? 2 : 1)}
        onToggleNegate={onToggleNegate}
        catalogue={catalogue}
        onChange={emit} onRemove={onRemove} />
    );
  }
  return (
    <RuleRow rule={inner} depth={depth} negated={negated}
      onToggleNegate={onToggleNegate}
      catalogue={catalogue}
      onChange={emit} onRemove={onRemove} />
  );
}

function GroupNode({
  node, depth, nodeDepth, catalogue, negated, onToggleNegate, onChange, onRemove,
}: {
  node: FilterGroup;
  depth: number;
  nodeDepth: number;
  catalogue: FilterCatalogue;
  negated: boolean;
  onToggleNegate?: () => void;
  onChange: (next: FilterGroup) => void;
  onRemove?: () => void;
}) {
  const setLogic = (logic: FilterLogic) => onChange({ ...node, logic });
  const addRule = () => onChange({ ...node, rules: [...node.rules, defaultRule(catalogue.fields)] });
  const addGroup = () => onChange({
    ...node, rules: [...node.rules, emptyGroup(node.logic === "AND" ? "OR" : "AND")],
  });
  const updateChild = (i: number, child: FilterRule | FilterGroup) => {
    const next = [...node.rules];
    next[i] = child;
    onChange({ ...node, rules: next });
  };
  const removeChild = (i: number) => {
    onChange({ ...node, rules: node.rules.filter((_, j) => j !== i) });
  };

  // A node reaches GroupNode with its own logic still "NOT" in exactly two
  // cases, both left read-only:
  //  1. A MALFORMED NOT anywhere in the tree (multi-child, empty, or
  //     double-negated) — ChildNode/unwrapRootNegation only unwrap
  //     well-formed ones. A multi-child NOT is evaluated partially by the
  //     engine (children[0] only) and rejected by the backend validator, so
  //     render it read-only and pass it through Apply byte-for-byte rather
  //     than silently normalising someone's saved filter.
  //  2. A well-formed NOT at the ROOT wrapping a bare LEAF rather than a
  //     group — the modal's `draft` state is always a FilterGroup, so
  //     unwrapping it would produce a FilterRule that has nowhere to live
  //     (see `unwrapRootNegation`); it stays wrapped and read-only for the
  //     same reason a malformed NOT does.
  //
  // "Read-only" here is scoped to this group's own shape: no AND/OR/NOT
  // toggle, no "Add rule"/"Add group". Its direct children still go through
  // the normal ChildNode path below — field, operator, value, and remove all
  // stay live — with one deliberate exception: they're passed
  // `negationLocked`, which withholds their own "Not" toggle. Negating a
  // child is the one edit that reaches back into a shape this component
  // already can't render editable (it would add or remove a layer of
  // negation this GroupNode was frozen specifically because it couldn't
  // safely represent), so it's withheld right alongside the rest of this
  // group's own controls rather than left dangling on the children.
  const isNot = node.logic === "NOT";

  return (
    <div className="adv-filter__group" data-depth={depth} data-logic={node.logic}>
      <div className="adv-filter__group-head">
        <div className="adv-filter__group-head-left">
          {onToggleNegate && (
            <NotToggle negated={negated} onToggle={onToggleNegate}
              title="Exclude everything this group matches" />
          )}
          {isNot ? (
            <span className="adv-filter__logic-not"
              aria-label="Negated group (shape locked; children stay editable, but not negatable)">Does not match</span>
          ) : (
            <div className="adv-filter__logic-toggle" role="group" aria-label="Combine with">
              <button type="button" aria-pressed={node.logic === "AND"}
                onClick={() => setLogic("AND")}>AND</button>
              <button type="button" aria-pressed={node.logic === "OR"}
                onClick={() => setLogic("OR")}>OR</button>
            </div>
          )}
        </div>
        {onRemove && (
          <button type="button" className="adv-filter__remove"
            onClick={onRemove} aria-label="Remove group">×</button>
        )}
      </div>
      <div className="adv-filter__group-body">
        {node.rules.map((child, i) => (
          <ChildNode key={i} node={child} depth={depth} nodeDepth={nodeDepth}
            catalogue={catalogue} negationLocked={isNot}
            onChange={(next) => updateChild(i, next)}
            onRemove={() => removeChild(i)} />
        ))}
        {node.rules.length === 0 && (
          <div className="adv-filter__empty">No rules yet — add one below.</div>
        )}
      </div>
      {!isNot && (
        <div className="adv-filter__group-actions">
          <button type="button" className="adv-filter__add" onClick={addRule}>
            + Add rule
          </button>
          {/* Gate on the depth the NEW GROUP's own child would occupy, not on
              whether the new group itself fits. A group only earns its keep
              if it can hold at least one rule: the new group lands at
              nodeDepth + 1, and a rule inside it lands at nodeDepth + 2, so
              require nodeDepth + 2 <= MAX_FILTER_DEPTH. Gating on nodeDepth
              < MAX_FILTER_DEPTH (the group's own fit) offers the button one
              level too deep — the group it creates has nowhere left to put
              a rule, and Apply stays disabled until it's deleted. */}
          {nodeDepth < MAX_FILTER_DEPTH - 1 && (
            <button type="button" className="adv-filter__add adv-filter__add--group"
              onClick={addGroup}>
              + Add group
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Split the modal's root `draft` into the group to render and whether it is
// wrapped in a well-formed root NOT. Reuses `unwrapNegation` rather than
// re-deriving negation shape: a root NOT wrapping a bare leaf, or a
// malformed NOT, doesn't unwrap into a FilterGroup — the type `draft` itself
// must always be — so those report `negated: false` and fall through to
// rendering `draft` as-is, which GroupNode already renders read-only (see
// its `isNot` branch). Only a root NOT wrapping a GROUP unwraps here, which
// is what lets that shape become editable instead of a dead end.
function unwrapRootNegation(
  draft: FilterGroup,
): { group: FilterGroup; negated: boolean } {
  const { inner, negated } = unwrapNegation(draft);
  if (negated && isGroup(inner)) return { group: inner, negated: true };
  return { group: draft, negated: false };
}

export function AdvancedFilterModal({ open, initial, onClose, onApply }: Props) {
  const { values: countries } = useCountries();
  const { fields, isLoading: fieldsLoading, error: fieldsError } = useFields();
  const fieldByKey = useMemo(
    () => new Map(fields.map((f) => [f.key, f])),
    [fields],
  );
  // One stable "options context" object for every node editor in the tree
  // (see `FilterCatalogue`) — keyed on its members so identity only changes
  // when the catalogue or country list actually change, not on every
  // keystroke inside the builder.
  const catalogue: FilterCatalogue = useMemo(
    () => ({ fields, fieldByKey, countries }),
    [fields, fieldByKey, countries],
  );
  // Derived directly from the `fields` this component already fetched above
  // (not via `useCatalogueLabels`, which exists for the OTHER consumers that
  // don't already hold the catalogue — calling it here too would be a
  // second, redundant subscription to the same data).
  const catalogueLabels = useMemo(() => labelsFromFields(fields), [fields]);
  const [draft, setDraft] = useState<FilterGroup>(initial);

  // Resync when the modal opens so an external filter change (e.g. AnaMoni)
  // shows the current state instead of stale draft.
  useEffect(() => {
    if (open) setDraft(initial);
  }, [open, initial]);

  const valid = draft.rules.length === 0 || isGroupComplete(draft);
  const fieldsValid = hasOnlyAvailableFilterFields(draft, fieldByKey);
  // The rule tree itself is only rendered once the catalogue is ready (see
  // the fieldsLoading/fieldsError guard below) — Apply must follow the same
  // gate, or a user could confirm a draft they can no longer see or edit.
  const fieldsReady = !fieldsLoading && !fieldsError && fields.some(isFilterField);
  // A tree can arrive already invalid for the backend (from the NL planner,
  // a saved view, or a NOT the builder keeps read-only because it can't be
  // authored here) before the user has touched "Add group" even once — no
  // in-builder gate catches that on its own, so back Apply up with a check
  // that mirrors both structural rules the backend enforces.
  const rejectionReason = backendRejectionReason(draft);
  const apply = () => { onApply(draft); onClose(); };
  const reset = () => setDraft(emptyGroup("AND"));

  // Root NOT handling (see `unwrapRootNegation`): when `draft` is itself a
  // well-formed NOT wrapping a group, render that inner group instead of the
  // read-only "Does not match" shell, exactly as ChildNode does for nested
  // NOTs. Edits inside the group must re-wrap in NOT so the negation isn't
  // silently dropped (mirrors ChildNode's `emit`); the toggle itself uses
  // `toggleNegation` directly, reusing the same wrap/unwrap `GroupNode`'s
  // nested NOT toggles rely on.
  const { group: rootGroup, negated: rootNegated } = unwrapRootNegation(draft);
  const onToggleRootNegate = rootNegated
    ? () => {
      const toggled = toggleNegation(draft);
      setDraft(isGroup(toggled) ? toggled : draft);
    }
    : undefined;
  const onRootGroupChange = (next: FilterGroup) =>
    setDraft(rootNegated ? { logic: "NOT", rules: [next] } : next);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Build advanced filter"
      width={760}
      okText="Apply filter"
      okButtonProps={{
        disabled: !valid || !fieldsValid || !fieldsReady || rejectionReason != null,
        "data-testid": "adv-filter-apply",
      }}
      onOk={apply}
      cancelText="Cancel"
      destroyOnHidden
      footer={(_, { OkBtn, CancelBtn }) => (
        <div className="adv-filter__footer">
          <button type="button" className="adv-filter__reset" onClick={reset}>
            Clear all
          </button>
          <div className="adv-filter__footer-right">
            <CancelBtn />
            <OkBtn />
          </div>
        </div>
      )}
    >
      <div className="adv-filter" data-testid="advanced-filter-modal">
        <p className="adv-filter__hint">
          Build a nested AND/OR rule tree across whitelisted INPACT fields.
          Use <strong>Not</strong> on any rule or group to exclude what it matches.
        </p>
        {draft.rules.length > 0 && (
          <div className="adv-filter__preview">
            <span className="adv-filter__preview-label">This will select:</span>
            <FilterTreePreview group={draft} catalogueLabels={catalogueLabels} />
          </div>
        )}
        {fieldsLoading ? (
          <p className="adv-filter__hint">Loading fields…</p>
        ) : !fieldsReady ? (
          <p className="adv-filter__warn" role="alert">
            Could not load filter fields. Please retry.
          </p>
        ) : (
          <GroupNode node={rootGroup} depth={0} nodeDepth={rootNegated ? 1 : 0}
            negated={rootNegated} onToggleNegate={onToggleRootNegate}
            catalogue={catalogue}
            onChange={onRootGroupChange} />
        )}
        {!valid && (
          <p className="adv-filter__warn" role="alert">
            Fill in a value for every rule before applying.
          </p>
        )}
        {!fieldsValid && (
          <p className="adv-filter__warn" role="alert">
            One or more filter fields are unavailable. Remove or replace them before applying.
          </p>
        )}
        {rejectionReason && (
          <p className="adv-filter__warn" role="alert">
            {rejectionReason}
          </p>
        )}
      </div>
    </Modal>
  );
}
